import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DATE_PRESET_DEFS } from 'utils/date';

type Option = { label: string; value: string };

export type FilterDef = {
  key: string;
  label: string;
  type: 'select' | 'multiselect' | 'dateRange';
  options: Option[];
};

export type AppliedFilter = {
  key: string;
  label: string;
  values: string[]; // store option labels for display; caller can map to values
};

type AdvancedFilterProps = {
  filters: FilterDef[];
  applied: AppliedFilter[];
  onChange: (next: AppliedFilter[]) => void;
  className?: string;
};

export const AdvancedFilter = ({
  filters,
  applied,
  onChange,
  className,
}: AdvancedFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Local draft to allow selecting multiple filters before a single Apply
  const [draft, setDraft] = useState<Record<string, AppliedFilter>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [datePreset, setDatePreset] = useState<string>('');
  // If not null, we are editing an existing filter; restrict UI to this key
  const [editModeKey, setEditModeKey] = useState<string | null>(null);
  const formatDateOnly = (d: Date) => {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const datePresets = DATE_PRESET_DEFS;
  const presetLabelMap: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    datePresets.forEach((p) => (map[p.id] = p.label));
    return map;
  }, [datePresets]);

  const activeFilterDef = useMemo(
    () => filters.find((f) => f.key === activeKey) || null,
    [activeKey, filters],
  );

  // Keep datePreset in sync with current draft whenever dateRange values change or panel opens
  useEffect(() => {
    if (activeFilterDef?.type !== 'dateRange') return;
    const key = activeFilterDef.key;
    const values = draft[key]?.values || [];
    if (values.length === 2 && values[0] === 'preset') {
      setDatePreset(values[1]);
    } else if (values.length === 3 && values[0] === 'custom') {
      setDatePreset('custom');
    } else {
      setDatePreset('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeKey, draft, activeFilterDef]);

  // Seed draft on open and close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveKey(null);
      }
    }
    if (isOpen) {
      const map: Record<string, AppliedFilter> = {};
      applied.forEach((a) => {
        if (a.key === 'dateRange') {
          const vals = a.values || [];
          if (vals.length === 1 && vals[0].startsWith('token:')) {
            const token = vals[0].replace('token:', '');
            map[a.key] = {
              key: a.key,
              label: a.label,
              values: ['preset', token],
            };
            return;
          }
          if (vals.length === 2 && vals[0] === 'preset') {
            const id =
              datePresets.find((p) => p.id === vals[1] || p.label === vals[1])
                ?.id || vals[1];
            map[a.key] = {
              key: a.key,
              label: a.label,
              values: ['preset', id],
            };
            return;
          }
          if (vals.length >= 2 && !isNaN(Date.parse(vals[0]))) {
            // legacy ISO strings
            const from = formatDateOnly(new Date(vals[0]));
            const to = formatDateOnly(new Date(vals[1] || vals[0]));
            map[a.key] = {
              key: a.key,
              label: a.label,
              values: ['custom', from, to],
            };
            return;
          }
          if (vals.length >= 3 && vals[0] === 'custom') {
            map[a.key] = {
              key: a.key,
              label: a.label,
              values: ['custom', vals[1], vals[2] || vals[1]],
            };
            return;
          }
        }
        map[a.key] = { ...a };
      });
      setDraft(map);
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, applied, datePresets]);

  const availableFilters = useMemo(() => {
    const appliedKeys = new Set(applied.map((a) => a.key));
    return filters.filter(
      (f) =>
        !appliedKeys.has(f.key) &&
        f.label.toLowerCase().includes(query.toLowerCase()),
    );
  }, [filters, query, applied]);

  const applyAll = () => {
    const normalized = Object.values(draft).map((entry) => {
      if (entry.key !== 'dateRange') return entry;
      const vals = entry.values || [];
      if (vals.length >= 2 && vals[0] === 'preset') {
        const id = vals[1];
        return {
          ...entry,
          values: ['preset', id],
        };
      }
      if (vals.length >= 3 && vals[0] === 'custom') {
        const from = vals[1];
        const to = vals[2];
        return {
          ...entry,
          values: ['custom', from, to || from],
        };
      }
      if (vals.length === 1 && vals[0].startsWith('token:')) {
        const token = vals[0].replace('token:', '');
        return {
          ...entry,
          values: ['preset', token],
        };
      }
      return entry;
    });
    onChange(normalized);
    setIsOpen(false);
    setActiveKey(null);
  };

  const clearAll = () => onChange([]);

  const removeFilter = (key: string) =>
    onChange(applied.filter((a) => a.key !== key));

  return (
    <div className={className} ref={containerRef}>
      <div className="flex items-center flex-wrap gap-2 mb-4">
        {applied.map((f) => (
          <span
            key={f.key}
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer"
            onClick={() => {
              setIsOpen(true);
              setActiveKey(f.key);
              setEditModeKey(f.key);
              const map: Record<string, AppliedFilter> = {};
              applied.forEach((a) => (map[a.key] = { ...a }));
              setDraft(map);
            }}
          >
            {(() => {
              const def = filters.find((d) => d.key === f.key);
              if (def?.type === 'dateRange') {
                const vals = f.values || [];
                if (vals.length === 1 && vals[0].startsWith('token:')) {
                  const token = vals[0].replace('token:', '');
                  const label = presetLabelMap[token] || token;
                  return (
                    <>
                      {f.label}: {label}
                    </>
                  );
                }
                if (vals.length >= 2 && vals[0] === 'preset') {
                  const id = vals[1];
                  const label = presetLabelMap[id] || id;
                  return (
                    <>
                      {f.label}: {label}
                    </>
                  );
                }
                if (vals.length >= 3 && vals[0] === 'custom') {
                  return (
                    <>
                      {f.label}: {vals[1]}
                      {vals[1] && vals[2] ? ' - ' : ''}
                      {vals[2]}
                    </>
                  );
                }
                return (
                  <>
                    {f.label}: {f.values.join(', ')}
                  </>
                );
              }
              return (
                <>
                  {f.label}: {f.values.join(', ')}
                </>
              );
            })()}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeFilter(f.key);
              }}
              className="ml-1 text-gray-600 hover:text-gray-900"
              aria-label={`Remove ${f.label}`}
            >
              ×
            </button>
          </span>
        ))}

        <button
          type="button"
          onClick={() => {
            setIsOpen((v) => !v);
            setEditModeKey(null);
            setActiveKey(null);
          }}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Add filter +
        </button>
        {applied.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="ml-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Clear all
          </button>
        )}
      </div>

      {isOpen && (
        <div className="relative z-40">
          <div
            className="absolute mt-2 flex bg-white border border-gray-200 rounded-md shadow-xl"
            // style={{ minWidth: 720 }}
          >
            {!editModeKey && (
              <div className="w-64 border-r border-gray-200 p-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search"
                  className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <ul className="max-h-60 overflow-auto">
                  {availableFilters.map((f) => {
                    const hasDraft = !!draft[f.key]?.values?.length;
                    return (
                      <li key={f.key}>
                        <button
                          className={`w-full flex justify-between items-center text-left px-3 py-2 text-sm hover:bg-gray-50 rounded ${
                            activeKey === f.key ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setActiveKey(f.key)}
                        >
                          <span>{f.label}</span>
                          {hasDraft && (
                            <span className="text-blue-700 text-xs">✓</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="w-64 p-2">
              {!activeFilterDef ? (
                <div className="text-sm text-gray-500 p-3">Choose a filter</div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="text-xs text-gray-500 px-1 mb-1">
                    {activeFilterDef.label}
                  </div>
                  {activeFilterDef.type === 'dateRange' ? (
                    <div className="px-1 py-1">
                      {(() => {
                        const vals = draft[activeFilterDef.key]?.values || [];
                        const mode = vals[0];
                        const fromValue =
                          mode === 'custom'
                            ? vals[1]
                            : vals[0] && vals.length === 2
                            ? vals[0]
                            : undefined;
                        const toValue =
                          mode === 'custom'
                            ? vals[2]
                            : vals.length === 2
                            ? vals[1]
                            : undefined;

                        const setRange = (from: Date, to: Date) => {
                          const fromTime = from.getTime();
                          const toTime = to.getTime();
                          const [start, end] =
                            fromTime <= toTime ? [from, to] : [to, from];
                          setDraft((prev) => ({
                            ...prev,
                            [activeFilterDef.key]: {
                              key: activeFilterDef.key,
                              label: activeFilterDef.label,
                              values: [
                                'custom',
                                formatDateOnly(start),
                                formatDateOnly(end),
                              ],
                            },
                          }));
                        };

                        const presets = datePresets;

                        const selectedPreset = datePreset;

                        return (
                          <div className="flex flex-col">
                            <div className="pr-4">
                              <ul className="space-y-2">
                                {presets.map((p) => (
                                  <li key={p.id}>
                                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datePreset"
                                        checked={selectedPreset === p.id}
                                        onChange={() => {
                                          setDraft((prev) => ({
                                            ...prev,
                                            [activeFilterDef.key]: {
                                              key: activeFilterDef.key,
                                              label: activeFilterDef.label,
                                              values: ['preset', p.id],
                                            },
                                          }));
                                          setDatePreset(p.id);
                                        }}
                                      />
                                      <span>{p.label}</span>
                                    </label>
                                  </li>
                                ))}
                                <li>
                                  <label className="flex items-center space-x-2 text-sm cursor-pointer">
                                    <input
                                      type="radio"
                                      name="datePreset"
                                      checked={selectedPreset === 'custom'}
                                      onChange={() => {
                                        const today = new Date();
                                        setDraft((prev) => ({
                                          ...prev,
                                          [activeFilterDef.key]: {
                                            key: activeFilterDef.key,
                                            label: activeFilterDef.label,
                                            values: [
                                              'custom',
                                              formatDateOnly(today),
                                              formatDateOnly(today),
                                            ],
                                          },
                                        }));
                                        setDatePreset('custom');
                                      }}
                                    />
                                    <span>Custom</span>
                                  </label>
                                </li>
                              </ul>
                            </div>
                            {selectedPreset === 'custom' && (
                              <div className="flex-1 mt-2">
                                <div className="text-xs text-gray-500 mb-1">
                                  Starting
                                </div>
                                <input
                                  type="date"
                                  className="w-full border rounded px-2 py-2 text-sm"
                                  value={fromValue || ''}
                                  onChange={(e) => {
                                    const start = e.target.value
                                      ? new Date(e.target.value + 'T00:00:00')
                                      : new Date();
                                    const end = toValue
                                      ? new Date(toValue + 'T00:00:00')
                                      : new Date();
                                    setRange(start, end);
                                  }}
                                />
                                <div className="text-xs text-gray-500 mt-3 mb-1">
                                  Ending
                                </div>
                                <input
                                  type="date"
                                  className="w-full border rounded px-2 py-2 text-sm"
                                  value={toValue || ''}
                                  onChange={(e) => {
                                    const start = fromValue
                                      ? new Date(fromValue + 'T00:00:00')
                                      : new Date();
                                    const end = e.target.value
                                      ? new Date(e.target.value + 'T00:00:00')
                                      : new Date();
                                    setRange(start, end);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <ul className="max-h-44 overflow-auto">
                      {activeFilterDef.options.map((opt) => {
                        const current =
                          draft[activeFilterDef.key]?.values || [];
                        const checked = current.includes(opt.label);
                        const toggle = () => {
                          const nextValues = checked
                            ? current.filter((v) => v !== opt.label)
                            : [...current, opt.label];
                          if (nextValues.length === 0) {
                            setDraft((prev) => {
                              const { [activeFilterDef.key]: _omit, ...rest } =
                                prev;
                              return rest;
                            });
                          } else {
                            setDraft((prev) => ({
                              ...prev,
                              [activeFilterDef.key]: {
                                key: activeFilterDef.key,
                                label: activeFilterDef.label,
                                values: nextValues,
                              },
                            }));
                          }
                        };
                        return (
                          <li key={opt.value} className="px-2 py-1">
                            <label className="flex items-center space-x-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={checked}
                                onChange={toggle}
                              />
                              <span>{opt.label}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="flex justify-between mt-auto">
                    <button
                      className="text-sm text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        if (editModeKey && activeFilterDef) {
                          // editing: clear only this filter from draft
                          setDraft((prev) => {
                            const { [activeFilterDef.key]: _omit, ...rest } =
                              prev;
                            return rest;
                          });
                        } else {
                          // adding: reset draft to currently applied (clear pending selections)
                          const map: Record<string, AppliedFilter> = {};
                          applied.forEach((a) => (map[a.key] = { ...a }));
                          setDraft(map);
                        }
                      }}
                    >
                      Clear
                    </button>
                    <div className="space-x-2">
                      <button
                        className="text-sm text-gray-600 hover:text-gray-900 px-2 py-1"
                        onClick={() => {
                          setIsOpen(false);
                          setActiveKey(null);
                          setEditModeKey(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                        onClick={applyAll}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilter;
